/**
 * Cross-subdomain auth cookie helpers.
 *
 * The buyer JWT must be readable across all tenant subdomains
 * (sugandhi.lvh.me, luru.lvh.me, grablocal.in, etc.).
 * localStorage is origin-scoped — it cannot do this.
 * A cookie with `domain=.<root-domain>` is shared across all subdomains.
 *
 * Domain derivation:
 *   - Browser: derived from `window.location.hostname` at runtime.
 *   - SSR / test: falls back to no-domain (fine — the cookie is still set).
 *
 * Security:
 *   - `SameSite=Lax` prevents CSRF while allowing normal navigation.
 *   - `Secure` is set when the page is served over HTTPS (production).
 *   - The token is a signed JWT — readable by JS is acceptable here since
 *     we don't have HttpOnly (which would require a server-side cookie API).
 */

const COOKIE_NAME = "buyer_token";
const MAX_AGE_SECONDS = 30 * 24 * 60 * 60; // 30 days (same as JWT exp)

/* ──────────────────────────────────────────────────────────────────────────
 * Generic cross-subdomain cookie helpers — shared with the cart session
 * cookie so a buyer's cart survives navigating between tenant storefronts
 * (sugandhi.lvh.me ⇄ luru.lvh.me).
 * ────────────────────────────────────────────────────────────────────────── */

export function setSharedCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === "undefined") return;
  const domain = rootDomain();
  const secure = isSecure() ? "; Secure" : "";
  const domainAttr = domain ? `; domain=${domain}` : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${domainAttr}${secure}`;
}

export function getSharedCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  try {
    return decodeURIComponent(match.split("=").slice(1).join("="));
  } catch {
    return null;
  }
}

export function clearSharedCookie(name: string): void {
  if (typeof document === "undefined") return;
  const domain = rootDomain();
  const domainAttr = domain ? `; domain=${domain}` : "";
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${domainAttr}`;
}

/** Return the root domain to share across subdomains (e.g. ".lvh.me", ".grablocal.in"). */
function rootDomain(): string {
  if (typeof window === "undefined") return "";
  const parts = window.location.hostname.split(".");
  // For "sugandhi.lvh.me" → ".lvh.me"
  // For "grablocal.in"    → ".grablocal.in"
  // For "localhost"       → "" (skip domain attr — cookies work fine)
  if (parts.length >= 2) {
    return "." + parts.slice(-2).join(".");
  }
  return "";
}

function isSecure(): boolean {
  return typeof window !== "undefined" && window.location.protocol === "https:";
}

export function setAuthCookie(token: string): void {
  setSharedCookie(COOKIE_NAME, token, MAX_AGE_SECONDS);
}

export function getAuthCookie(): string | null {
  return getSharedCookie(COOKIE_NAME);
}

export function clearAuthCookie(): void {
  clearSharedCookie(COOKIE_NAME);
}
